'use client'

import clsx from 'clsx'
import * as Flags from 'country-flag-icons/react/3x2'
import { useLocale, useTranslations } from 'next-intl'

import {
  useAnalyticsAudience,
  useAnalyticsFanGrowth
} from '@/features/clubs/hooks/useClubAnalytics'
import { buildFanGrowthChart } from '@/features/clubs/lib/athleteProfile'
import { maxShareKey } from '@/features/clubs/lib/clubAnalytics'

import FanGrowthS from '../../ClubProfileStats/components/FanGrowthCard/FanGrowthCard.module.scss'
import S from '../ClubProfileAnalytics.module.scss'
import BlockState from './BlockState'
import MiniBarRow from './MiniBarRow'

type FlagComponent = React.ComponentType<
  React.SVGProps<SVGSVGElement> & { title?: string }
>
const FLAG_COMPONENTS = Flags as unknown as Record<string, FlagComponent>

const MAX_BAR = 160
const MIN_BAR = 8

const regionName = (iso2: string, fallback: string, locale: string) => {
  try {
    return (
      new Intl.DisplayNames([locale], { type: 'region' }).of(iso2) ?? fallback
    )
  } catch {
    return fallback
  }
}

const FanGrowth = () => {
  const t = useTranslations('club.stats')
  const locale = useLocale()
  const { data, isLoading, isError, refetch } = useAnalyticsFanGrowth('week')
  const buckets = data ?? []
  const chart = buildFanGrowthChart(buckets, 'week', locale)
  const weekday = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    timeZone: 'UTC'
  })
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC'
  })
  const first = buckets.at(0)
  const last = buckets.at(-1)
  const lastIndex = chart.bars.length - 1

  return (
    <div className={S.growthPart}>
      <header className={FanGrowthS.header}>
        <span className={FanGrowthS.title}>{t('fanGrowth')}</span>
        {first && last && chart.total > 0 ? (
          <span className={S.rangeText}>
            {`${day.format(new Date(first.start))} – ${day.format(
              new Date(last.start)
            )}`}
          </span>
        ) : null}
      </header>
      {isError ? (
        <BlockState error large onRetry={() => refetch()} />
      ) : isLoading ? (
        <div className={S.chartSkeleton} aria-busy="true" />
      ) : chart.total === 0 ? (
        <BlockState
          large
          title={t('analytics.empty.fanGrowth')}
          description={t('analytics.empty.fanGrowthText')}
        />
      ) : (
        <div
          className={clsx(FanGrowthS.chart, S.growthChart)}
          role="img"
          aria-label={t('fanGrowthSummary', { count: chart.total })}
        >
          {chart.bars.map((bar, index) => (
            <div
              key={bar.key}
              className={FanGrowthS.barCol}
              title={`${bar.value}`}
            >
              {index === lastIndex ? (
                <div className={FanGrowthS.barValue}>{bar.value}</div>
              ) : null}
              <div
                className={clsx(
                  FanGrowthS.bar,
                  index === lastIndex && FanGrowthS.barHighlight
                )}
                style={{
                  height: `${Math.max(
                    MIN_BAR,
                    Math.round(bar.ratio * MAX_BAR)
                  )}px`
                }}
              />
              <div className={FanGrowthS.dayLabel}>
                {weekday.format(new Date(bar.key))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const Location = () => {
  const t = useTranslations('club.stats')
  const locale = useLocale()
  const { data, isError, refetch } = useAnalyticsAudience()
  const countries = data?.location ?? []
  const other = data?.location_other
  const top = maxShareKey(
    countries.map(c => ({ key: c.iso2 ?? c.name, count: c.count, percent: 0 }))
  )

  return (
    <div className={S.locationPart}>
      <header className={FanGrowthS.header}>
        <span className={FanGrowthS.title}>{t('analytics.location')}</span>
      </header>
      {isError ? (
        <BlockState error large onRetry={() => refetch()} />
      ) : !data ? (
        <div className={S.chartSkeleton} aria-busy="true" />
      ) : countries.length === 0 ? (
        <BlockState
          large
          title={t('analytics.noLocation')}
          description={t('analytics.empty.locationText')}
        />
      ) : (
        <div className={clsx(S.miniCharts, S.locationCharts)}>
          {countries.map(country => {
            const Flag = country.iso2
              ? FLAG_COMPONENTS[country.iso2]
              : undefined
            const name = country.iso2
              ? regionName(country.iso2, country.name, locale)
              : country.name
            return (
              <MiniBarRow
                key={country.iso2 ?? country.name}
                item={{
                  label: name,
                  icon: Flag ? <Flag className={S.locationFlagImage} /> : null,
                  percent: country.percent,
                  count: country.count,
                  active: (country.iso2 ?? country.name) === top
                }}
              />
            )
          })}
          {other && other.count > 0 ? (
            <MiniBarRow
              item={{
                label: t('analytics.other'),
                percent: other.percent,
                count: other.count
              }}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

/** Fan growth of the last 7 days and Location in one card (design). */
const GrowthLocationCard = () => {
  const t = useTranslations('club.stats')
  return (
    <section
      className={`${FanGrowthS.card} ${S.growthLocationCard}`}
      aria-label={`${t('fanGrowth')}, ${t('analytics.location')}`}
    >
      <div className={`${FanGrowthS.liveContent} ${S.growthLocationContent}`}>
        <FanGrowth />
        <Location />
      </div>
    </section>
  )
}

export default GrowthLocationCard
